// src/modules/recipes/api.ts
// Recipe & SOP Management — API client and TypeScript types

// ============================================================================
// Types
// ============================================================================

export type RecipeType =
  | 'Base Sauce'
  | 'Chutney'
  | 'Prep Item'
  | 'Menu Item'
  | 'Assembly Recipe'
  | 'Dessert'
  | 'Drink'
  | 'Breakfast'
  | 'Other';

export type RecipeStatus = 'Draft' | 'Active' | 'Archived';

export const RECIPE_TYPES: RecipeType[] = [
  'Base Sauce', 'Chutney', 'Prep Item', 'Menu Item',
  'Assembly Recipe', 'Dessert', 'Drink', 'Breakfast', 'Other',
];

export const RECIPE_STATUSES: RecipeStatus[] = ['Draft', 'Active', 'Archived'];

export const STATIONS = ['Prep', 'Fryer', 'Assembly', 'Beverage', 'Dessert', 'Kitchen'] as const;
export type Station = typeof STATIONS[number];

export interface Recipe {
  id: number;
  name: string;
  recipe_type: RecipeType;
  category: string | null;
  yield_quantity: number | null;
  yield_unit: string | null;
  prep_time_minutes: number | null;
  cook_time_minutes: number | null;
  shelf_life: string | null;
  storage_instructions: string | null;
  status: RecipeStatus;
  version: number;
  notes: string | null;
  cogs_recipe_id: number | null;
  created_by: number | null;
  updated_by: number | null;
  created_by_name?: string | null;
  updated_by_name?: string | null;
  ingredient_count?: number;
  step_count?: number;
  created_at: string;
  updated_at: string;
}

export interface RecipeIngredient {
  id: number;
  recipe_id: number;
  ingredient_type: 'Raw Material' | 'Sub Recipe';
  ingredient_id: number | null;
  sub_recipe_id: number | null;
  ingredient_name?: string | null;
  ingredient_default_unit?: string | null;
  sub_recipe_name?: string | null;
  sub_recipe_type?: string | null;
  quantity: number | null;
  unit: string | null;
  quantity_text: string | null;
  is_unscalable: boolean;
  needs_review: boolean;
  preparation_note: string | null;
  is_optional: boolean;
  is_costable: boolean;
  sort_order: number;
  // Scaling
  scaled_quantity?: number | null;
  warning?: string | null;
}

export interface RecipeStep {
  id: number;
  recipe_id: number;
  step_number: number;
  instruction: string;
  station: Station | null;
  staff_note: string | null;
  critical_control_point: boolean;
  sort_order: number;
}

export interface RecipeVersion {
  id: number;
  recipe_id: number;
  version: number;
  changed_fields: string[] | null;
  updated_by: number | null;
  updated_by_name: string | null;
  user_name?: string | null;
  created_at: string;
}

export interface RecipeVersionDetail extends RecipeVersion {
  snapshot: {
    recipe: Recipe;
    ingredients: RecipeIngredient[];
    steps: RecipeStep[];
    snapshot_at: string;
  };
}

export interface RecipeDetail {
  recipe: Recipe;
  ingredients: RecipeIngredient[];
  steps: RecipeStep[];
}

export interface RecipeScaleResult {
  recipe: Recipe;
  scaled_ingredients: RecipeIngredient[];
  multiplier: number;
  original_yield_quantity: number | null;
  original_yield_unit: string | null;
  scaled_yield_quantity: number | null;
}

export interface ImportSheetCandidate {
  name: string;
  ingredients: Array<{
    ingredient_name: string;
    quantity_text: string;
    issues: string[];
    fatal: boolean;
  }>;
  steps: Array<{ instruction: string }>;
}

export interface ImportSheet {
  sheet_name: string;
  candidate_recipes: ImportSheetCandidate[];
  loose_ingredients: Array<{
    ingredient_name: string;
    quantity_text: string;
    issues: string[];
  }>;
}

export interface ImportJob {
  id: number;
  file_name: string | null;
  status: 'pending' | 'previewed' | 'imported' | 'cancelled';
  import_summary: any | null;
  created_by: number | null;
  created_at: string;
  updated_at: string;
}

export interface ImportPreviewResult {
  job: ImportJob;
  sheets: ImportSheet[];
  sheet_count: number;
  total_candidate_recipes: number;
}

export interface RecipeFilters {
  status?: RecipeStatus;
  recipe_type?: RecipeType;
  category?: string;
  search?: string;
}

// ============================================================================
// Helpers
// ============================================================================

const headers = (token: string | null, extra?: Record<string, string>) => ({
  'X-Session-Token': token || '',
  ...(extra || {}),
});

async function handle<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const text = await res.text();
    let msg = text;
    try { msg = JSON.parse(text).error || text; } catch { /* leave text */ }
    throw new Error(msg || `HTTP ${res.status}`);
  }
  return res.json() as Promise<T>;
}

// ============================================================================
// Recipes
// ============================================================================

export const listRecipes = (token: string | null, filters: RecipeFilters = {}) => {
  const qs = new URLSearchParams();
  if (filters.status) qs.set('status', filters.status);
  if (filters.recipe_type) qs.set('recipe_type', filters.recipe_type);
  if (filters.category) qs.set('category', filters.category);
  if (filters.search) qs.set('search', filters.search);
  const q = qs.toString() ? `?${qs}` : '';
  return fetch(`/api/sop/recipes${q}`, { cache: 'no-store', headers: headers(token) })
    .then(handle<{ recipes: Recipe[]; total: number }>);
};

export const getRecipe = (token: string | null, id: number) =>
  fetch(`/api/sop/recipes/${id}`, { cache: 'no-store', headers: headers(token) })
    .then(handle<RecipeDetail>);

export const scaleRecipe = (token: string | null, id: number, multiplier: number) =>
  fetch(`/api/sop/recipes/${id}/scale?multiplier=${multiplier}`, {
    cache: 'no-store', headers: headers(token),
  }).then(handle<RecipeScaleResult>);

export const createRecipe = (
  token: string | null,
  body: Partial<Recipe> & { ingredients?: Partial<RecipeIngredient>[]; steps?: Partial<RecipeStep>[] }
) =>
  fetch('/api/sop/recipes', {
    method: 'POST',
    headers: headers(token, { 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
  }).then(handle<{ recipe: Recipe }>);

export const updateRecipe = (
  token: string | null,
  id: number,
  body: Partial<Recipe> & { ingredients?: Partial<RecipeIngredient>[]; steps?: Partial<RecipeStep>[] }
) =>
  fetch(`/api/sop/recipes/${id}`, {
    method: 'PATCH',
    headers: headers(token, { 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
  }).then(handle<{ recipe: Recipe }>);

export const archiveRecipe = (token: string | null, id: number) =>
  fetch(`/api/sop/recipes/${id}/archive`, {
    method: 'POST', headers: headers(token),
  }).then(handle<{ recipe: Recipe }>);

export const activateRecipe = (token: string | null, id: number) =>
  fetch(`/api/sop/recipes/${id}/activate`, {
    method: 'POST', headers: headers(token),
  }).then(handle<{ recipe: Recipe }>);

// ============================================================================
// Version History
// ============================================================================

export const getRecipeVersions = (token: string | null, recipeId: number) =>
  fetch(`/api/sop/recipes/${recipeId}/versions`, {
    cache: 'no-store', headers: headers(token),
  }).then(handle<{ recipe: Recipe; versions: RecipeVersion[]; total: number }>);

// ============================================================================
// Import
// ============================================================================

export const listImportJobs = (token: string | null) =>
  fetch('/api/sop/import', { cache: 'no-store', headers: headers(token) })
    .then(handle<{ jobs: ImportJob[] }>);

export const parseImportFile = (token: string | null, file_base64: string, file_name: string) =>
  fetch('/api/sop/import', {
    method: 'POST',
    headers: headers(token, { 'Content-Type': 'application/json' }),
    body: JSON.stringify({ file_base64, file_name }),
  }).then(handle<ImportPreviewResult>);

export const confirmImport = (
  token: string | null,
  jobId: number,
  selected_recipes?: string[]
) =>
  fetch(`/api/sop/import/${jobId}/confirm`, {
    method: 'POST',
    headers: headers(token, { 'Content-Type': 'application/json' }),
    body: JSON.stringify({ selected_recipes }),
  }).then(handle<{ summary: { created: number; skipped: number; errors: any[] } }>);

export const cancelImport = (token: string | null, jobId: number) =>
  fetch(`/api/sop/import/${jobId}/cancel`, {
    method: 'POST', headers: headers(token),
  }).then(handle<{ job: ImportJob }>);
